import React, { useEffect, useMemo, useRef, useState } from "react";

const categories = ["All", "Bags & cases", "Accessories", "Objects"];

const getCategory = (name) => {
  const value = name.toLowerCase();
  if (/bag|case|cover|holder|luggage|wallet|backpack/.test(value)) return "Bags & cases";
  if (/belt|bracelet|cufflink|ear ring|hat|omamori|pin|tie clip|watch/.test(value)) return "Accessories";
  return "Objects";
};

const Product = ({ data }) => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const closeButtonRef = useRef(null);
  const openerRef = useRef(null);
  const products = useMemo(() => data?.items || [], [data]);
  const visibleProducts = useMemo(
    () => products.filter((product) => activeCategory === "All" || getCategory(product.productName) === activeCategory),
    [activeCategory, products]
  );

  useEffect(() => {
    if (!selectedProduct) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setSelectedProduct(null);
      if (event.key === "Tab") {
        event.preventDefault();
        closeButtonRef.current?.focus();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      openerRef.current?.focus();
    };
  }, [selectedProduct]);

  if (!data) return null;

  const openProduct = (product, event) => {
    openerRef.current = event.currentTarget;
    setSelectedProduct(product);
  };

  return (
    <section id="product" className="product-section" aria-labelledby="product-heading">
      <div className="product-head">
        <p className="section-index">01</p>
        <h2 id="product-heading">{data.heading}</h2>
        <p className="product-total">Collection · {products.length}</p>
      </div>

      <div className="product-toolbar" aria-label="Filter products by category">
        <div className="product-filters">
          {categories.map((category) => (
            <button
              className={`filter-button${activeCategory === category ? " is-active" : ""}`}
              key={category}
              type="button"
              aria-pressed={activeCategory === category}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
        <p className="product-result-count" aria-live="polite">
          {String(visibleProducts.length).padStart(2, "0")}
        </p>
      </div>

      <div className="product-grid">
        {visibleProducts.map((product) => {
          const originalIndex = products.indexOf(product);
          const category = getCategory(product.productName);

          return (
            <article className="product-card" key={product.productName}>
              <button
                className="product-image-button"
                type="button"
                aria-label={`View ${product.productName}`}
                onClick={(event) => openProduct(product, event)}
              >
                <img
                  alt={`${product.productName} — ${category.toLowerCase()} design`}
                  src={`images/portfolio/${product.imageFilename}`}
                  loading="lazy"
                />
                <span className="product-view-cue" aria-hidden="true">Open</span>
              </button>
              <div className="product-meta">
                <div>
                  <p className="product-category">{category}</p>
                  <h3>{product.productName}</h3>
                </div>
                <span className="product-number" aria-label={`Item ${originalIndex + 1}`}>
                  {String(originalIndex + 1).padStart(2, "0")}
                </span>
              </div>
            </article>
          );
        })}
      </div>

      {selectedProduct && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lightbox-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelectedProduct(null);
          }}
        >
          <div className="lightbox-frame">
            <div className="lightbox-bar">
              <p id="lightbox-title">{selectedProduct.productName}</p>
              <button
                ref={closeButtonRef}
                className="lightbox-close"
                type="button"
                aria-label="Close image viewer"
                onClick={() => setSelectedProduct(null)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" /></svg>
              </button>
            </div>
            <div className="lightbox-image-wrap">
              <img
                src={`images/portfolio/${selectedProduct.imageFilename}`}
                alt={`${selectedProduct.productName} enlarged view`}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Product;
